import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getActionableRequests,
  getActionableRequestsInBackground,
  getRequestOptions,
  updateStatusRequests,
} from "../../slices/requests";
import { useSearchParams } from "react-router-dom";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import Filters from "../layout/Filters";
import {
  BoltIcon,
  BoltSlashIcon,
  CheckBadgeIcon,
  CheckIcon,
  ExclamationCircleIcon,
  HandRaisedIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { getColour } from "../../utils/colours";
import Badge from "../layout/Badge";
import ActionButtons from "../layout/ActionButtons";
import { useActionableRequests } from "../../contexts/ActionableRequestsProvider";
import { translateText } from "../../utils/translation";
import Table from "../layout/Table";
import { RoomState, getRooms, updateRoom } from "../../slices/rooms";
import { Cog8ToothIcon } from "@heroicons/react/24/outline";
import {
  getUnixTimestampNow,
  getDifferenceBetweenUnixTimestampsMilliseconds,
} from "../../utils/datetime";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import DisabledTextInput from "../forms/DisabledTextInput";
import Tags from "../forms/Tags";
import SwitchToggle from "../forms/SwitchToggle";

export default function RoomsOverview() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchCategory = searchParams.get("category");
  const searchTag = searchParams.get("tag");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getActionableRequests({
        pageNum: false,
        category: searchCategory,
        dateRange: dateRangeTerm,
      }),
    );
    dispatch(getRequestOptions());
    dispatch(getRooms());
  }, [dispatch, searchCategory, dateRangeTerm]);

  // Set state
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [tag, setTag] = useState({ value: "" });
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [selectedEditRoom, setSelectedEditRoom] = useState<RoomState | null>(
    null,
  );
  const [showEditRoomModal, setShowEditRoomModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const {
    actionable,
    request_options: requestOptions,
    options,
  } = useAppSelector((state) => state.requests);
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "UK" : user.language;

  // Timed call to backend for updated requests every 60 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      if (actionable.loading === false) {
        dispatch(
          getActionableRequestsInBackground({
            pageNum: false,
            category: searchCategory,
            dateRange: dateRangeTerm,
          }),
        );
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [actionable.loading, dateRangeTerm, dispatch, searchCategory]);

  // Get context from providers
  const { isOnline } = useActionableRequests();

  // Functions
  function selectRequest(requestId: string) {
    const updatedSelectedRequests = [...selectedRequests];
    if (!updatedSelectedRequests.includes(requestId)) {
      updatedSelectedRequests.push(requestId);
    } else {
      updatedSelectedRequests.splice(
        updatedSelectedRequests.indexOf(requestId),
        1,
      );
    }
    setSelectedRequests(updatedSelectedRequests);
  }

  function selectAllRequests(requestId: any) {
    const currentRequestIds =
      actionable.data === null
        ? []
        : actionable.data.map((request) => request.id);

    // If all are selected, unselect all
    if (
      selectedRequests.length > 0 &&
      selectedRequests.length === currentRequestIds.length
    ) {
      setSelectedRequests([]);
      return;
    }

    // Otherwise, select all
    const requestIds =
      actionable.data === null
        ? []
        : actionable.data.map((request) => request.id);
    setSelectedRequests(requestIds);
  }

  async function markRequestsAsComplete(e: any, args: any[]) {
    e.preventDefault();
    const [requestIds] = args;
    const res: any = await dispatch(
      updateStatusRequests({ requestIds, status: "Complete" }),
    );
    if (res.payload.status === 200) {
      setSelectedRequests([]);
      dispatch(
        addAlert(
          generateAlertText("update_request_status_complete", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(
          generateAlertText("update_request_status_error", language),
          "danger",
          3000,
        ),
      );
    }
  }

  async function markRequestsAsInProgress(e: any, args: any[]) {
    e.preventDefault();
    const [requestIds] = args;
    const res: any = await dispatch(
      updateStatusRequests({ requestIds, status: "In Progress" }),
    );
    if (res.payload.status === 200) {
      setSelectedRequests([]);
      dispatch(
        addAlert(
          generateAlertText("update_request_status_in_progress", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(
          generateAlertText("update_request_status_error", language),
          "danger",
          3000,
        ),
      );
    }
  }

  function resetSelectedRoom(e: any) {
    e.preventDefault(0);
    setSelectedRoom(null);
    setSelectedRequests([]);
  }

  function getNumberReleventRequestsRoom(roomId: string) {
    return actionableData.filter((request) => request.room.id === roomId)
      .length;
  }

  function createCardText(roomId: string) {
    const total = getNumberReleventRequestsRoom(roomId);
    return `${total} ${total === 1
      ? translateText("actionable_request", language)
      : translateText("actionable_requests", language)
      }`;
  }

  function calculateRoomTimeDelta(roomId: string) {
    const relevantData = actionableData.filter(
      (request) => request.room.id === roomId,
    );
    const madeRequest = options.rooms_made_request.filter(
      (x: any) => x.id === roomId,
    )[0];
    if (relevantData.length === 0) {
      if (madeRequest === undefined) return { diff: 0, colour: "white" };
      if (madeRequest.made_request === true)
        return { diff: 0, colour: "success" };
      return { diff: 0, colour: "white" };
    }
    const timeDeltas = relevantData.map(
      (request) => request.datetime_created.timestamp,
    );
    const mostRecent = Math.min(...timeDeltas);
    const unixNow = getUnixTimestampNow();
    const diff = getDifferenceBetweenUnixTimestampsMilliseconds(
      unixNow,
      mostRecent,
    );
    const orangeLimit = user === null ? 5 : user.property.orange_limit;
    const redLimit = user === null ? 10 : user.property.red_limit;
    if (diff < orangeLimit) return { diff, colour: "white" };
    if (diff >= orangeLimit && diff < redLimit)
      return { diff, colour: "warning" };
    return { diff, colour: "danger" };
  }

  function selectRoomToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [roomId] = args;
    const roomData =
      rooms.data === null
        ? null
        : rooms.data.filter((room) => room.id === roomId)[0];
    setSelectedEditRoom(roomData);
    setShowEditRoomModal(true);
  }

  function updateRequestOptionForRoom(requestId: string) {
    const currentRequestOptions =
      selectedEditRoom === null ? [] : selectedEditRoom.request_options;
    const currentRequestOptionsIds = currentRequestOptions.map(
      (x: any) => x.id,
    );
    if (currentRequestOptionsIds.includes(requestId)) {
      const updatedRequestOptions = currentRequestOptions.filter(
        (x: any) => x.id !== requestId,
      );
      if (selectedEditRoom !== null)
        setSelectedEditRoom({
          ...selectedEditRoom,
          request_options: updatedRequestOptions,
        });
    } else {
      const requestOptionToAdd =
        requestOptions.data === null
          ? null
          : requestOptions.data.filter((x: any) => x.id === requestId)[0];
      if (requestOptionToAdd === null || requestOptionToAdd === undefined)
        return;
      const formattedRequestOptionToAdd = {
        id: requestOptionToAdd.id,
        category: requestOptionToAdd.category,
        translation: requestOptionToAdd.translation,
      };
      const updatedRequestOptions = [
        ...currentRequestOptions,
        formattedRequestOptionToAdd,
      ];
      if (selectedEditRoom !== null)
        setSelectedEditRoom({
          ...selectedEditRoom,
          request_options: updatedRequestOptions,
        });
    }
  }

  function selectOrUnselectAll(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableOptions =
        requestOptions.data === null
          ? []
          : requestOptions.data.map((x) => ({
            id: x.id,
            category: x.category,
            translation: x.translation,
          }));
      if (selectedEditRoom !== null)
        setSelectedEditRoom({
          ...selectedEditRoom,
          request_options: allAvailableOptions,
        });
    } else {
      if (selectedEditRoom !== null)
        setSelectedEditRoom({ ...selectedEditRoom, request_options: [] });
    }
  }

  async function confirmEditRoom(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    if (selectedEditRoom === null) {
      dispatch(
        addAlert(generateAlertText("missing_room", language), "danger", 5000),
      );
      return;
    }
    const {
      id: roomId,
      name,
      room_type: roomType,
      tag,
      request_options: requestOptions,
      allow_announcements: allowAnnouncements,
      floor,
    } = selectedEditRoom;
    const formattedRequestOptions = requestOptions.map((x: any) => x.id);
    const obj = {
      roomId,
      name,
      property: user.property.id,
      roomType,
      tag,
      requestOptions: formattedRequestOptions,
      allowAnnouncements,
      floor,
    };
    const res: any = await dispatch(updateRoom(obj));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_room", language), "success", 3000),
      );
      setShowEditRoomModal(false);
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
    }
  }

  function getSelectedRequestsText(numRequests: number) {
    if (language === "EN") {
      if (numRequests === 1) return `${numRequests} request selected`;
      return `${numRequests} requests selected`;
    }
    if (language === "FR") {
      if (numRequests === 1) return `${numRequests} demande sélectionnée`;
      return `${numRequests} demandes sélectionnées`;
    }
    if (language === "IT") {
      if (numRequests === 1) return `${numRequests} richiesta selezionata`;
      return `${numRequests} richieste selezionate`;
    }
    if (numRequests === 1) return `${numRequests} request selected`;
    return `${numRequests} requests selected`;
  }

  // Format rooms data and filter by query param tag
  let roomsData = rooms.data === null ? [] : rooms.data;
  if (searchTag !== null) {
    roomsData = roomsData.filter((room) => room.tag.includes(searchTag));
  }

  // Create tags object for filters
  let roomTags: any = [];
  for (let i = 0; i < roomsData.length; i++) {
    const candidateTags = roomsData[i].tag;
    for (let j = 0; j < candidateTags.length; j++) {
      if (!roomTags.includes(candidateTags[j])) {
        roomTags.push(candidateTags[j]);
      }
    }
  }
  roomTags.sort();
  roomTags = roomTags.map((x: any) => ({ value: x, label: x }));

  // Filter actionable request data based on selected room
  let actionableData = actionable.data === null ? [] : actionable.data;
  const actionableQueue = actionable.queue === null ? [] : actionable.queue;
  actionableData = [...actionableQueue, ...actionableData];
  const selectedActionableData = actionableData.filter(
    (request) => request.room.id === selectedRoom,
  );

  // Create list of all possible request options within property
  const possibleRequestOptions =
    requestOptions.data === null ? [] : requestOptions.data;
  const possibleRequestOptionsHierarchy =
    options.requests === undefined
      ? []
      : Object.keys(options.requests).map((option: any) => {
        let validRequestOptions = possibleRequestOptions.filter(
          (x: any) => x.category === option,
        );
        validRequestOptions.sort((a, b) =>
          a.translation.localeCompare(b.translation),
        );
        return {
          category: option,
          requests: validRequestOptions,
        };
      });

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("rooms_overview_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`inline-flex justify-center rounded-full p-2 text-sm bg-white text-secondary hover:bg-white/90 active:bg-white/90 active:text-black pointer-events-none`}
              >
                {isOnline ? (
                  <BoltIcon
                    className="h-5 w-5 text-success"
                    aria-hidden="true"
                  />
                ) : (
                  <BoltSlashIcon
                    className="h-5 w-5 text-danger"
                    aria-hidden="true"
                  />
                )}
              </Button>
            }
          />
        }
      />

      {/* Edit room modal */}
      <Modal
        show={showEditRoomModal}
        setShow={setShowEditRoomModal}
        confirmText={translateText("edit_room", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditRoom}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`${translateText(
                "edit_room",
                language,
              )} "${selectedEditRoom?.name}"`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Created at */}
              <DisabledTextInput
                id="datetime_created"
                label={translateText("created_at", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedEditRoom === null
                    ? ""
                    : selectedEditRoom.datetime_created
                }
              />

              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedEditRoom}
                stateName="name"
                onChange={setSelectedEditRoom}
              />

              {/* Devices */}
              <DisabledTextInput
                id="devices"
                label={translateText("devices", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedEditRoom === null
                    ? ""
                    : selectedEditRoom.devices.join(", ")
                }
              />

              {/* Tags */}
              <Tags
                id="tag"
                label={translateText("tag", language)}
                small={translateText("tag_small", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                placeholder={translateText("tag_placeholder", language)}
                showLabel={true}
                state={selectedEditRoom}
                stateName="tag"
                handler={setSelectedEditRoom}
              />

              {/* Request options */}
              <div>
                <label className="mb-1 block text-left text-sm font-medium text-black">
                  {translateText("request_options", language)}
                </label>
                <p className="text-left text-xs italic text-secondary">
                  {translateText("request_options_subtext", language)}
                </p>
              </div>

              {/* Select/unselect all buttons */}
              <div className="grid grid-cols-2">
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-success hover:bg-success/30 hover:text-success-dark`}
                  onClick={selectOrUnselectAll}
                  onClickArgs={[true]}
                >
                  <CheckIcon
                    className={`h-5 w-5 text-success group-hover:text-success-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-success-dark group-hover:text-success-dark">
                    {translateText("select_all", language)}
                  </span>
                </Button>
                <Button
                  type="button"
                  className={`group inline-flex justify-center rounded-lg py-2 px-3 text-sm font-semibold outline-2 outline-offset-2 transition-colors bg-transparent text-warning hover:bg-warning/30 hover:text-warning-dark`}
                  onClick={selectOrUnselectAll}
                  onClickArgs={[false]}
                >
                  <XMarkIcon
                    className={`h-5 w-5 text-warning group-hover:text-warning-dark mr-1`}
                    aria-hidden="true"
                  />
                  <span className="text-warning group-hover:text-warning-dark">
                    {translateText("unselect_all", language)}
                  </span>
                </Button>
              </div>
              <div className="grid grid-cols-1 space-y-8">
                {possibleRequestOptionsHierarchy.map((category: any) => {
                  const readableCategory =
                    options.requests[category.category][language];
                  return (
                    <div key={`${category.category}-request-options`}>
                      <div className="text-left text-sm underline">
                        <span>{readableCategory}</span>
                      </div>
                      <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                        {category.requests.map((request: any) => {
                          return (
                            <Checkbox
                              key={request.id}
                              id={request.id}
                              onChange={updateRequestOptionForRoom}
                              checked={
                                selectedEditRoom === null
                                  ? false
                                  : selectedEditRoom.request_options.filter(
                                    (x: any) => x.id === request.id,
                                  ).length > 0
                              }
                              label={request.translation}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Allow announcements */}
              <SwitchToggle
                id="allow_announcements"
                label={translateText("allow_announcements", language)}
                showLabel={true}
                description={translateText(
                  "allow_announcements_description",
                  language,
                )}
                toggle={
                  selectedEditRoom === null
                    ? false
                    : selectedEditRoom.allow_announcements
                }
                state={selectedEditRoom}
                stateName="allow_announcements"
                handler={setSelectedEditRoom}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {actionable.loading || requestOptions.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "tag",
                  name: translateText("tag", language),
                  options: roomTags,
                },
              ]}
              queryParams={[
                ["dateRange", dateRangeTerm],
                ["tag", searchTag],
              ]}
              handlers={{
                tag: {
                  state: tag,
                  stateName: "value",
                  onChange: setTag,
                },
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
              }}
              loading={actionable.loading}
              baseUrl="/rooms/overview"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {roomsData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {roomsData.map((room) => {
                    const { colour } = calculateRoomTimeDelta(room.id);
                    return (
                      <Card
                        key={room.id}
                        colour={colour}
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset ${colour === "danger" && "animate-bounce"
                          }`}
                      >
                        {/* Card header */}
                        <div
                          className={`border-b border-${colour === "white"
                            ? "light"
                            : colour === "success"
                              ? "white"
                              : colour === "warning"
                                ? "black"
                                : colour === "danger" && "white"
                            } pb-2`}
                        >
                          <div className="items-center justify-between">
                            <div className="flex">
                              <h3
                                className={`flex-grow text-base font-semibold leading-6 text-${colour === "white"
                                  ? "black"
                                  : colour === "success"
                                    ? "white"
                                    : colour === "warning"
                                      ? "black"
                                      : colour === "danger" && "white"
                                  } truncate`}
                              >
                                {room.name}
                              </h3>
                              <div>
                                <Button
                                  type="button"
                                  className={`group inline-flex justify-center rounded-full text-sm bg-transparent`}
                                  onClick={selectRoomToEdit}
                                  onClickArgs={[room.id]}
                                >
                                  <Cog8ToothIcon
                                    className={`h-6 w-6 text-${colour === "white"
                                      ? "secondary"
                                      : colour === "success"
                                        ? "white"
                                        : colour === "warning"
                                          ? "black"
                                          : colour === "danger" && "white"
                                      } group-hover:text-${colour === "white"
                                        ? "black"
                                        : colour === "warning"
                                          ? "white"
                                          : colour === "danger" && "black"
                                      } my-auto`}
                                    aria-hidden="true"
                                  />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Card body */}
                        <a
                          key={room.id}
                          href="#!"
                          onClick={() => setSelectedRoom(room.id)}
                        >
                          <div className="pt-2">
                            <p
                              className={`text-sm text-${colour === "white"
                                ? "secondary"
                                : colour === "success"
                                  ? "white"
                                  : colour === "warning"
                                    ? "black"
                                    : colour === "danger" && "white"
                                }`}
                            >
                              {createCardText(room.id)}
                            </p>
                          </div>
                        </a>
                      </Card>
                    );
                  })}
                </div>

                {/* Requests table for selected room */}
                {selectedRoom !== null && (
                  <div className="mt-16">
                    {selectedActionableData.length === 0 ? (
                      <NoContent
                        text={translateText("no_requests", language)}
                        subtext={translateText("no_requests_subtext", language)}
                        icon={
                          <ExclamationCircleIcon
                            className="mx-auto h-12 w-12 text-secondary animate-bounce"
                            aria-hidden="true"
                          />
                        }
                      />
                    ) : (
                      <div className="space-y-2">
                        <div className="row flex justify-end">
                          <Button
                            type="button"
                            className={`rounded-full p-2 bg-transparent text-black hover:bg-white/50`}
                            onClick={resetSelectedRoom}
                          >
                            <XMarkIcon
                              className="h-5 w-5 text-black"
                              aria-hidden="true"
                            />
                          </Button>
                        </div>

                        {(selectedRequests.length > 0 ||
                          actionableQueue.length > 0) && (
                            <div className="mx-1">
                              {selectedRequests.length > 0 && (
                                <p className="text-sm text-granite italic">
                                  {getSelectedRequestsText(
                                    selectedRequests.length,
                                  )}
                                </p>
                              )}
                              <div
                                className={`mt-2 mb-4 grid grid-cols-2 space-y-2 md:space-y-0 gap-x-2`}
                              >
                                {/* Mark as complete & In progress */}
                                {selectedRequests.length > 0 && (
                                  <>
                                    <Button
                                      type="button"
                                      className={`${getButtonStyle(
                                        "solid",
                                        "success",
                                      )}`}
                                      onClick={markRequestsAsComplete}
                                      onClickArgs={[selectedRequests]}
                                    >
                                      <CheckBadgeIcon
                                        className="w-5 h-5 mr-2"
                                        aria-hidden="true"
                                      />
                                      {translateText("complete", language)}
                                    </Button>
                                    <Button
                                      type="button"
                                      className={`${getButtonStyle(
                                        "solid",
                                        "warning",
                                      )}`}
                                      onClick={markRequestsAsInProgress}
                                      onClickArgs={[selectedRequests]}
                                    >
                                      <HandRaisedIcon
                                        className="w-5 h-5 mr-2"
                                        aria-hidden="true"
                                      />
                                      {translateText("in_progress", language)}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        <Table
                          header={
                            <tr>
                              <th className="px-2 py-3">
                                <Checkbox
                                  id="select-all"
                                  onChange={selectAllRequests}
                                  checked={
                                    selectedRequests.length > 0 &&
                                    selectedRequests.length ===
                                    selectedActionableData.length
                                  }
                                />
                              </th>
                              <th className="px-2 py-3">
                                {translateText("created", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("category", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("request", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("detail", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("quantity", language)}
                              </th>
                              <th className="px-2 py-3">
                                {translateText("status", language)}
                              </th>
                            </tr>
                          }
                          body={selectedActionableData.map((request) => (
                            <tr
                              key={request.id}
                              className={`${selectedRequests.includes(request.id) &&
                                "bg-light/20"
                                } hover:bg-light/20`}
                              onClick={() => selectRequest(request.id)}
                            >
                              <td className="border-r border-light/50 px-2 py-3">
                                <Checkbox
                                  id={request.id}
                                  onChange={selectRequest}
                                  checked={selectedRequests.includes(
                                    request.id,
                                  )}
                                />
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.datetime_created.string}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                <Badge
                                  text={request.category[1]}
                                  bgColour={getColour(request.category[0])}
                                  textColour={`${getColour(
                                    request.category[0],
                                  )}-dark`}
                                  ringColour={`${getColour(
                                    request.category[0],
                                  )}-dark`}
                                />
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.request}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.details}
                              </td>
                              <td className="border-r border-light/50 px-2 py-3 whitespace-nowrap">
                                {request.quantity}
                              </td>
                              <td className="px-2 py-3">
                                <Badge
                                  text={request.status[1]}
                                  bgColour={getColour(request.status[0])}
                                  textColour={`${getColour(
                                    request.status[0],
                                  )}-dark`}
                                  ringColour={`${getColour(
                                    request.status[0],
                                  )}-dark`}
                                />
                              </td>
                            </tr>
                          ))}
                          showPagination={false}
                          paginationHasPrevious={
                            actionable.pagination.has_previous
                          }
                          paginationHasNext={actionable.pagination.has_next}
                          paginationCurrentPage={
                            actionable.pagination.current_page
                          }
                          paginationTotalPages={
                            actionable.pagination.total_pages
                          }
                          paginationTotalObjects={
                            actionable.pagination.total_objects
                          }
                          paginationQueryParams={["category", "dateRange"]}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_rooms", language)}
                subtext={translateText("no_rooms_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
